import React, { useEffect, useState } from 'react';
import { 
  ChartBarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';

interface AnalyticsData {
  revenue: {
    today: number;
    week: number;
    month: number;
    total: number;
  };
  orders: {
    total: number;
    completed: number;
    in_progress: number;
    cancelled: number;
    completion_rate: number;
  };
  users: {
    customers: number;
    dispatchers: number;
    pending_dispatchers: number;
  };
}

const PlatformAnalytics: React.FC = () => {
  const { formatPrice } = useCurrency();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/admin/analytics', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-6 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-white py-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'No data available'}</p>
          <button 
            onClick={fetchAnalytics}
            className="mt-4 btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Platform Analytics</h1>
          <p className="mt-2 text-gray-600 font-poppins">View revenue and usage statistics</p>
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-poppins">Today's Revenue</p>
                <p className="mt-2 text-2xl font-bold text-green-600 font-poppins">
                  {formatPrice(analytics.revenue.today)}
                </p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-poppins">This Week</p>
                <p className="mt-2 text-2xl font-bold text-green-600 font-poppins">
                  {formatPrice(analytics.revenue.week)}
                </p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-poppins">This Month</p>
                <p className="mt-2 text-2xl font-bold text-green-600 font-poppins">
                  {formatPrice(analytics.revenue.month)}
                </p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <ChartBarIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-poppins">Total Revenue</p>
                <p className="mt-2 text-2xl font-bold text-green-600 font-poppins">
                  {formatPrice(analytics.revenue.total)}
                </p>
                <p className="mt-1 text-xs text-gray-500">All time</p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 font-poppins mb-4">Order Statistics</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-sky-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Total Orders</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.orders.total.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Completed</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.orders.completed.toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-green-600 font-semibold font-poppins">
                  {analytics.orders.completion_rate}%
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">In Progress</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.orders.in_progress.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-red-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Cancelled</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.orders.cancelled.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 font-poppins mb-4">User Growth</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-sky-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Customers</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.users.customers.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Active Dispatchers</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.users.dispatchers.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                <div className="flex items-center">
                  <TruckIcon className="h-8 w-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 font-poppins">Pending Approval</p>
                    <p className="text-xl font-bold text-gray-900 font-poppins">
                      {analytics.users.pending_dispatchers.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAnalytics;
